import * as Linking from 'expo-linking';
import { Platform } from 'react-native';

// ---------------------------------------------------------------------------
// Deep-link invites: a 1-tap link replaces a hand-typed referral code.
//
// We mint and parse `tashkeela://referral/<CODE>` (the app's custom scheme,
// from app.json). Opening it routes to the referral screen with the code
// pre-filled in the redeem field - the user still taps Confirm, we never
// auto-redeem from a link.
// ---------------------------------------------------------------------------

/**
 * A referral code is exactly 8 chars from the unambiguous alphabet the server
 * mints (A-Z minus I/L/O, plus 2-9). Validating it - rather than trusting any
 * string smuggled in via a crafted link - stops `..`/query segments or other
 * junk from being routed or echoed back into the input.
 */
const REFERRAL_CODE_RE = /^[A-HJ-NP-Z2-9]{8}$/;

/** Normalise then validate a candidate code. Returns null when it isn't one. */
export function normalizeReferralCode(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return REFERRAL_CODE_RE.test(code) ? code : null;
}

/** The shareable invite link for a code, e.g. `tashkeela://referral/ABCD2345`. */
export function buildReferralLink(code: string): string {
  // Linking.createURL respects the configured scheme (and any expo path prefix
  // in dev), so the link is correct in Expo Go, dev builds and production.
  return Linking.createURL(`referral/${code}`);
}

/**
 * Pull a referral code out of an inbound deep-link URL. Handles both shapes:
 *   - path style:  tashkeela://referral/<CODE>
 *   - query style: tashkeela://referral?code=<CODE>  (also /referral?code=)
 * Returns the validated code, or null when the URL isn't a referral invite.
 */
export function referralCodeFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: ReturnType<typeof Linking.parse>;
  try {
    parsed = Linking.parse(url);
  } catch {
    return null;
  }
  const path = (parsed.path ?? '').replace(/^\/+/, '');
  const segments = path.split('/').filter(Boolean);
  if (segments[0] !== 'referral') return null;

  // Prefer the path segment; fall back to a ?code= query param.
  const fromPath = segments[1];
  const queryCode = parsed.queryParams?.code;
  const fromQuery = Array.isArray(queryCode) ? queryCode[0] : queryCode;
  return normalizeReferralCode(fromPath ?? fromQuery ?? null);
}

/**
 * Watch for inbound referral links and hand the validated code to `onCode`.
 * Covers a warm tap (listener) AND a cold start (the URL that launched the
 * app). Returns a cleanup. Web is handled by expo-router directly, so this
 * no-ops there to avoid double-routing.
 */
export function listenForReferralLinks(onCode: (code: string) => void): () => void {
  if (Platform.OS === 'web') return () => {};
  void Linking.getInitialURL()
    .then((url) => {
      const code = referralCodeFromUrl(url);
      if (code) onCode(code);
    })
    .catch(() => {});
  const sub = Linking.addEventListener('url', ({ url }) => {
    const code = referralCodeFromUrl(url);
    if (code) onCode(code);
  });
  return () => sub.remove();
}

// ---------------------------------------------------------------------------
// Friend-league / 1v1 invites: the same 1-tap pattern, for joining a private
// league. We mint and parse `tashkeela://league/<CODE>`; opening it routes to
// the Friend Leagues screen with the code pre-filled in the join field - the
// user still taps Join, we never auto-join from a link.
// ---------------------------------------------------------------------------

/**
 * A friend-league invite code is exactly 12 chars from the alphabet the server
 * mints (`private.generate_invite_code`): A-Z minus I/L/O, plus 2-9. Validating
 * it - rather than trusting any string smuggled in via a crafted link - stops
 * `..`/query junk from being routed or echoed back into the input.
 */
const LEAGUE_CODE_RE = /^[A-HJ-NP-Z2-9]{12}$/;

/** Normalise then validate a candidate league code. Null when it isn't one. */
export function normalizeLeagueInviteCode(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return LEAGUE_CODE_RE.test(code) ? code : null;
}

/** The shareable invite link for a code, e.g. `tashkeela://league/ABCDEFGH2345`. */
export function buildLeagueInviteLink(code: string): string {
  return Linking.createURL(`league/${code}`);
}

/**
 * Pull a league invite code out of an inbound deep-link URL. Handles both:
 *   - path style:  tashkeela://league/<CODE>
 *   - query style: tashkeela://league?code=<CODE>
 * Returns the validated code, or null when the URL isn't a league invite.
 */
export function leagueInviteCodeFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: ReturnType<typeof Linking.parse>;
  try {
    parsed = Linking.parse(url);
  } catch {
    return null;
  }
  const path = (parsed.path ?? '').replace(/^\/+/, '');
  const segments = path.split('/').filter(Boolean);
  if (segments[0] !== 'league') return null;

  // Prefer the path segment; fall back to a ?code= query param.
  const fromPath = segments[1];
  const queryCode = parsed.queryParams?.code;
  const fromQuery = Array.isArray(queryCode) ? queryCode[0] : queryCode;
  return normalizeLeagueInviteCode(fromPath ?? fromQuery ?? null);
}

/**
 * Watch for inbound league invite links and hand the validated code to `onCode`.
 * Covers a warm tap (listener) AND a cold start (the launching URL). Returns a
 * cleanup. Web is handled by expo-router directly, so this no-ops there to avoid
 * double-routing.
 */
export function listenForLeagueInviteLinks(onCode: (code: string) => void): () => void {
  if (Platform.OS === 'web') return () => {};
  void Linking.getInitialURL()
    .then((url) => {
      const code = leagueInviteCodeFromUrl(url);
      if (code) onCode(code);
    })
    .catch(() => {});
  const sub = Linking.addEventListener('url', ({ url }) => {
    const code = leagueInviteCodeFromUrl(url);
    if (code) onCode(code);
  });
  return () => sub.remove();
}
